import { React, useEffect, useState } from "react";
import {
  TableContainer,
  TableCaption,
  Table,
  Thead,
  Tr,
  Td,
  Th,
  Tbody,
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionButton,
  Box,
  AccordionIcon,
  Button,
  Skeleton,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getBookings } from "../../services/bookings";
import { useWindowSize } from "../../utils/customHooks";
import BookingDetailsModal from "./BookingDetailsModal";
import { formatDateTimeCET } from "../../utils/helpers";
import AdminBookingSourceCell, {
  adminBookingRowBackground,
} from "../../components/admin-booking-source/AdminBookingSourceCell";

function AllBookings() {
  const { t } = useTranslation("common");
  const { width } = useWindowSize();
  const [currentPage, setCurrentPage] = useState(1);
  const [isAppending, setIsAppending] = useState(false);
  const [fetchNew, setFetchNew] = useState(false);
  const [bookingsData, setBookingsData] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);

  const { data, isSuccess, isFetching, refetch } = useQuery(
    ["bookings", currentPage],
    () =>
      getBookings({
        page: !fetchNew ? currentPage : 1,
      }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      select: (data) => data.data,
      onSuccess: (data) => {
        // Prepend new data to the top
        const existingIds = new Set(bookingsData.map((booking) => booking.id));
        const filteredNewData = data.results.filter(
          (booking) => !existingIds.has(booking.id)
        );
        if (isAppending) {
          // Append new data to the end
          setBookingsData((prevBookings) => [
            ...prevBookings,
            ...filteredNewData,
          ]);
        } else {
          setBookingsData((prevBookings) => [
            ...filteredNewData,
            ...prevBookings,
          ]);
        }

        // Reset appending and fetchNew flags
        setFetchNew(false);
        setIsAppending(false);
      },
      enabled: true,
    }
  );

  useEffect(() => {
    if (data?.pagination.nextPage && data?.pagination.totalPages > currentPage)
      setHasNextPage(true);
    else setHasNextPage(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (fetchNew) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNew]);

  if (isSuccess) {
    if (width > 768) {
      return (
        <>
          <Button
            isLoading={fetchNew}
            onClick={() => {
              setFetchNew(true);
            }}
          >
            Refetch
          </Button>
          <TableContainer overflowX="unset" overflowY="unset">
            <Table variant="simple">
              <TableCaption>{t("allReservations")}</TableCaption>
              <Thead position="sticky" top={0}>
                <Tr>
                  <Th
                    backgroundColor="lightcoral"
                    position={"sticky"}
                    left={"0"}
                  >
                    Id
                  </Th>
                  <Th>{t("adminBookedBy")}</Th>
                  <Th>{t("adminTraveler")} name</Th>
                  <Th>{t("adminTraveler")} surname</Th>
                  <Th>Guest telephone number</Th>
                  <Th>E-mail</Th>
                  <Th>Start date</Th>
                  <Th>End date</Th>
                  <Th>Price</Th>
                  <Th>Reservation type</Th>
                  <Th>Driver included</Th>
                  <Th>Booking details</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {fetchNew && (
                  <>
                    <Tr>
                      <Td colSpan="100%">
                        <Skeleton height="20px" />
                      </Td>
                    </Tr>
                    <Tr>
                      <Td colSpan="100%">
                        <Skeleton height="20px" />
                      </Td>
                    </Tr>
                  </>
                )}
                {bookingsData.map((booking) => (
                  <Tr key={booking.id} bg={adminBookingRowBackground(booking)}>
                    <Td
                      backgroundColor="lightcoral"
                      position={"sticky"}
                      left={"0"}
                    >
                      <strong>{booking.referenceNumber}</strong>
                    </Td>
                    <Td>
                      <AdminBookingSourceCell booking={booking} />
                    </Td>
                    <Td>{booking.client?.firstName}</Td>
                    <Td>{booking.client?.lastName}</Td>
                    <Td>{booking.client?.telephoneNumber}</Td>
                    <Td>{booking.client?.email}</Td>
                    <Td>{formatDateTimeCET(booking.checkInDate)}</Td>
                    <Td>{formatDateTimeCET(booking.checkOutDate)}</Td>
                    <Td>{booking.fullPrice}</Td>
                    <Td>
                      {booking.accommodation
                        ? "stay"
                        : booking.car
                        ? "car"
                        : "tour"}
                    </Td>
                    <Td>{booking.driverIncluded ? "Yes" : "No"}</Td>

                    {booking.bookingType === "tour" ||
                    booking.bookingType === "stay" ? (
                      <Td>
                        <BookingDetailsModal
                          tour={booking.tour}
                          accommodationUnits={booking.accommodationUnits}
                          accommodation={booking.accommodation}
                        />
                      </Td>
                    ) : (
                      <Td>{booking.bookingDetailsList}</Td>
                    )}
                    <Td>{booking.currentStatus}</Td>
                  </Tr>
                ))}
                {/* Skeletons for loading state */}
                {isFetching && !fetchNew && (
                  <>
                    <Tr>
                      <Td colSpan="100%">
                        <Skeleton height="20px" />
                      </Td>
                    </Tr>
                    <Tr>
                      <Td colSpan="100%">
                        <Skeleton height="20px" />
                      </Td>
                    </Tr>
                  </>
                )}
              </Tbody>
            </Table>
          </TableContainer>
          {hasNextPage && (
            <Button
              isLoading={isFetching && !fetchNew}
              onClick={() => {
                setIsAppending(true);
                setCurrentPage(currentPage + 1);
              }}
            >
              Show more
            </Button>
          )}
        </>
      );
    } else {
      return (
        <>
          <Button isLoading={fetchNew} onClick={() => setFetchNew(true)}>
            Refetch
          </Button>
          <Accordion defaultIndex={[0]} allowMultiple>
            {fetchNew && <Skeleton height={"50px"}></Skeleton>}
            {bookingsData.map((booking, index) => (
              <AccordionItem key={booking.id}>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left">
                      <strong style={{ fontWeight: "bold" }}>
                        {booking.referenceNumber}
                      </strong>{" "}
                      <AdminBookingSourceCell booking={booking} compact />{" "}
                      {booking.client?.firstName} {booking.client?.lastName}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <Box as="span" flex="1" textAlign="left">
                    <Box mb={2}>
                      {t("adminBookedBy")}:{" "}
                      <AdminBookingSourceCell booking={booking} />
                    </Box>
                    <p>Telephone number: {booking.client?.telephoneNumber}</p>
                    <p>E-mail: {booking.client?.email}</p>
                    <p>Check-in date: {formatDateTimeCET(booking.checkInDate)}</p>
                    <p>Check-out date: {formatDateTimeCET(booking.checkOutDate)}</p>
                    <p>FullPrice: {booking.fullPrice}</p>
                    <p>
                      Reservation type:{" "}
                      {booking.accommodation
                        ? "stay"
                        : booking.car
                        ? "car"
                        : "tour"}
                    </p>
                    <p>
                      Driver inlcuded: {booking.driverIncluded ? "Yes" : "No"}
                    </p>

                    <p>Booking details:</p>
                    {booking.bookingType === "tour" ||
                    booking.bookingType === "stay" ? (
                      <p>
                        <BookingDetailsModal
                          tour={booking.tour}
                          accommodationUnits={booking.accommodationUnits}
                          accommodation={booking.accommodation}
                        />
                      </p>
                    ) : (
                      <p>{booking.bookingDetailsList}</p>
                    )}
                    <p>Status: {booking.currentStatus}</p>
                  </Box>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
          {isFetching && !fetchNew && <Skeleton height={"50px"}></Skeleton>}

          {hasNextPage && (
            <Button
              isLoading={isFetching && !fetchNew}
              onClick={() => {
                setIsAppending(true);
                setCurrentPage(currentPage + 1);
              }}
            >
              Show more
            </Button>
          )}
        </>
      );
    }
  }
}

export default AllBookings;
